'use client';

import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import { useCallbackRef } from '../../../hooks/use-callback-ref';
import type { ToastPropsInterface } from '../../toast/types/toast.types';
import type {
  ToastObjectInterface,
  UseToastQueueArgumentsInterface,
  UseToastQueueReturnTypeInterface,
} from '../types/use-toast-queue.types';

export const useToastQueue = (
  opts: UseToastQueueArgumentsInterface = {},
): UseToastQueueReturnTypeInterface => {
  const { maxSize = 3 } = opts;

  const [activeIds, setActiveIds] = useState<Array<number>>([]);
  const [pendingIds, setPendingIds] = useState<Array<number>>([]);

  const toasts = useRef<Record<number, ToastObjectInterface>>({});
  const id = useRef(-1);

  const pause = useCallbackRef((toastId: number) => {
    const toast = toasts.current[toastId];

    if (!toast || !toast.timeout) {
      return;
    }

    clearTimeout(toast.timeout);
    delete toast.timeout;
    toast.elapsed += Date.now() - toast.start;
  });

  const resume = useCallbackRef((toastId: number) => {
    const toast = toasts.current[toastId];

    if (!toast || toast.timeout) {
      return;
    }

    // Note: A duration of `0` should not initiate a dismiss timeout
    if (toast.options?.duration) {
      const handleDismiss = () => {
        // oxlint-disable-next-line react/immutability -- Timer runs after dismiss is initialized.
        dismiss(toastId);
      };
      toast.timeout = setTimeout(handleDismiss, toast.options.duration - toast.elapsed);
    }
  });

  const pauseAll = useCallbackRef(() => {
    activeIds.forEach(pause);
  });

  const resumeAll = useCallbackRef(() => {
    activeIds.forEach(resume);
  });

  const clearToast = useCallbackRef((toastId: number) => {
    const toast = toasts.current[toastId];

    if (toast?.timeout) {
      clearTimeout(toast.timeout);
    }

    const { [toastId]: _clearedToast, ...remainingToasts } = toasts.current;
    toasts.current = remainingToasts;
  });

  const clearAllToasts = useCallbackRef(() => {
    Object.keys(toasts.current).forEach((toastId) => {
      clearToast(Number(toastId));
    });
  });

  const dismiss = useCallbackRef((toastId: number) => {
    if (typeof toastId === 'undefined') return;

    if (activeIds.includes(toastId)) {
      setActiveIds(activeIds.filter((tid) => tid !== toastId));
    }

    if (pendingIds.includes(toastId)) {
      setPendingIds(pendingIds.filter((tid) => tid !== toastId));
    }

    clearToast(toastId);
  });

  const dismissAll = useCallbackRef(() => {
    clearAllToasts();
    setActiveIds([]);
    setPendingIds([]);
  });

  // Moves toast from pending to active and initiates its timeout
  useEffect(() => {
    if (pendingIds.length && activeIds.length < maxSize) {
      const [toastId, ...pendingTail] = pendingIds;
      // oxlint-disable-next-line react/set-state-in-effect -- Queue promotion follows committed queue state.
      setActiveIds([...activeIds, toastId]);
      setPendingIds(pendingTail);
      resume(toastId);
    }
  }, [pendingIds, activeIds, maxSize, resume]);

  // Merges duration options from Provider props and enqueue() options
  const getToastDuration = useCallbackRef(
    ({
      tone,
      providerProps,
      enqueueProps,
    }: {
      tone?: ToastPropsInterface['tone'];
      providerProps?: UseToastQueueArgumentsInterface;
      enqueueProps?: UseToastQueueArgumentsInterface;
    }): number => {
      const defaultDuration = 0;
      const duration = enqueueProps?.duration ?? providerProps?.duration ?? defaultDuration;

      if (typeof duration === 'number') {
        return duration;
      }

      if (tone && tone in duration) {
        return duration[tone] ?? defaultDuration;
      }

      return defaultDuration;
    },
  );

  const enqueue = useCallbackRef(
    (node: ReactElement<ToastPropsInterface>, options?: UseToastQueueArgumentsInterface) => {
      id.current += 1;
      const toastId = id.current;

      const toast: ToastObjectInterface = {
        id: toastId,
        elapsed: 0,
        start: Date.now(),
        options: {
          ...opts,
          ...options,
          duration: getToastDuration({
            tone: node?.props?.tone ?? 'info',
            providerProps: opts,
            enqueueProps: options,
          }),
        },
        node,
      };

      toasts.current[toastId] = toast;
      setPendingIds([...pendingIds, toastId]);
    },
  );

  const currentToasts = useMemo(() => {
    // oxlint-disable-next-line react/refs -- ID state drives renders; the ref stores the matching toast records.
    return activeIds.flatMap((toastId) => {
      const toast = toasts.current[toastId];
      return toast ? [toast] : [];
    });
  }, [activeIds]);

  const unfilteredToasts = useMemo(() => {
    // oxlint-disable-next-line react/refs -- ID state drives renders; the ref stores the matching toast records.
    return [...activeIds, ...pendingIds].flatMap((toastId) => {
      const toast = toasts.current[toastId];
      return toast ? [toast] : [];
    });
  }, [activeIds, pendingIds]);

  useEffect(() => {
    return () => {
      clearAllToasts();
    };
  }, [clearAllToasts]);

  /*
   * Memoize the returned object so the provider's context value
   * receives a stable reference whenever none of its members change.
   * Without this, every render of the caller would produce a fresh
   * object literal and defeat downstream `React.memo` / context-value
   * equality checks — see CDS-5649 (FIX-3).
   */
  return useMemo(
    () => ({
      enqueue,
      dismissAll,
      dismiss,
      toasts: currentToasts,
      unfilteredToasts,
      activeIds,
      pendingIds,
      pause,
      pauseAll,
      resume,
      resumeAll,
    }),
    [
      enqueue,
      dismissAll,
      dismiss,
      currentToasts,
      unfilteredToasts,
      activeIds,
      pendingIds,
      pause,
      pauseAll,
      resume,
      resumeAll,
    ],
  );
};
